import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:pointer-events-none',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogVariant = 'sheet' | 'fullscreen';

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideCloseButton?: boolean;
  /**
   * Mobile presentation. Default 'sheet' = bottom-anchored grabber sheet (centered look,
   * rounded top, max-h 90dvh, safe-area). Use 'fullscreen' for long forms / multi-step
   * flows that need the entire viewport (ItemFormModal, ImportCollectr, crop tools…).
   * Desktop is always a centered modal regardless of this prop.
   */
  variant?: DialogVariant;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton = false, variant = 'sheet', ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const closeRef = React.useRef<HTMLButtonElement | null>(null);
  const dragCleanupRef = React.useRef<() => void>(() => {});
  const dragStateRef = React.useRef({ y: 0, t: 0, dy: 0, dragging: false, pointerId: null as number | null });

  const setRefs = React.useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [ref]);

  const beginHandleDrag = React.useCallback((clientY: number, pointerId: number | null, preventDefault: () => void) => {
    const el = contentRef.current;
    if (variant !== 'sheet' || !el) return;

    const resetDrag = () => {
      dragStateRef.current = { y: 0, t: 0, dy: 0, dragging: false, pointerId: null };
    };

    dragCleanupRef.current();
    dragStateRef.current = { y: clientY, t: performance.now(), dy: 0, dragging: false, pointerId };
    preventDefault();

    const finishByDistance = () => {
      const { dy, dragging, t } = dragStateRef.current;
      resetDrag();
      dragCleanupRef.current();

      const dt = Math.max(1, performance.now() - t);
      const velocity = dy / dt;
      const shouldClose = dy > 80 || velocity > 0.5;
      el.style.transition = '';
      el.style.transform = '';
      if (shouldClose || !dragging) closeRef.current?.click();
    };

    function moveTo(nextY: number, preventMoveDefault: () => void) {
      const dy = nextY - dragStateRef.current.y;
      if (dy <= 0 && !dragStateRef.current.dragging) return;
      preventMoveDefault();
      dragStateRef.current.dragging = true;
      dragStateRef.current.dy = Math.max(0, dy);
      el.style.transition = 'none';
      el.style.transform = `translateY(${dragStateRef.current.dy}px)`;
    }

    function onMove(e: PointerEvent) {
      if (e.pointerId !== dragStateRef.current.pointerId) return;
      moveTo(e.clientY, () => e.preventDefault());
    }

    function onTouchMove(e: TouchEvent) {
      const touch = e.touches[0];
      if (!touch) return;
      moveTo(touch.clientY, () => e.preventDefault());
    }

    function finish(e: PointerEvent) {
      if (e.pointerId !== dragStateRef.current.pointerId) return;
      finishByDistance();
    }

    function finishTouch() {
      finishByDistance();
    }

    window.addEventListener('pointermove', onMove, { passive: false, capture: true });
    window.addEventListener('pointerup', finish, { passive: true, capture: true });
    window.addEventListener('pointercancel', finish, { passive: true, capture: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    window.addEventListener('touchend', finishTouch, { passive: true, capture: true });
    window.addEventListener('touchcancel', finishTouch, { passive: true, capture: true });

    dragCleanupRef.current = () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', finish, true);
      window.removeEventListener('pointercancel', finish, true);
      window.removeEventListener('touchmove', onTouchMove, true);
      window.removeEventListener('touchend', finishTouch, true);
      window.removeEventListener('touchcancel', finishTouch, true);
      dragCleanupRef.current = () => {};
    };
  }, [variant]);

  React.useEffect(() => () => dragCleanupRef.current(), []);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRefs}
        className={cn(
          // ===== MOBILE ===== (solid surface — touch drawer must stay opaque)
          variant === 'fullscreen'
            ? // Long-form: fullscreen flush
              'fixed inset-0 z-50 flex flex-col w-screen h-[100dvh] min-w-full pt-[max(env(safe-area-inset-top),0.5rem)] pb-[max(env(safe-area-inset-bottom),0.5rem)] bg-[#101116]'
            : // Default sheet: bottom-anchored, grabber, rounded top, safe-area
              'fixed inset-x-0 bottom-0 z-50 flex flex-col w-full max-h-[90dvh] rounded-t-2xl border-t border-white/[0.06] pt-3 pb-[max(env(safe-area-inset-bottom),1rem)] bg-[#101116] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          // ===== DESKTOP (centered modal — glass by default) =====
          'sm:inset-auto sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:w-auto sm:h-auto sm:min-h-0 sm:min-w-0 sm:max-w-lg sm:max-h-[90dvh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-md sm:border sm:pt-8 sm:pb-8 sm:px-6 sm:gap-4 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-98 sm:data-[state=open]:zoom-in-98',
          'sm:bg-black/55 sm:backdrop-blur-2xl sm:border-white/[0.1] sm:shadow-2xl',
          // ===== Shared =====
          'px-4 border-white/[0.06] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.6),inset_0_1px_1px_rgba(255,255,255,0.03)] duration-200 ease-out',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'overscroll-y-contain text-white overflow-hidden',
          className
        )}
        {...props}
      >
        {/* Mobile grab pill (sheet variant only) */}
        {variant === 'sheet' && (
          <div
            aria-label="Close drawer"
            role="button"
            tabIndex={0}
            onPointerDown={(e) => beginHandleDrag(e.clientY, e.pointerId, () => e.preventDefault())}
            onTouchStart={(e) => {
              if ('PointerEvent' in window) return;
              const touch = e.touches[0];
              if (touch) beginHandleDrag(touch.clientY, null, () => e.preventDefault());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                closeRef.current?.click();
              }
            }}
            onDragStart={(e) => e.preventDefault()}
            style={{ touchAction: 'none' }}
            className="sm:hidden -mx-4 -mt-3 mb-1 flex h-10 shrink-0 cursor-grab items-center justify-center select-none active:cursor-grabbing [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none]"
          >
            <div className="h-1.5 w-12 rounded-full bg-white/25 pointer-events-none select-none" />
          </div>
        )}
        {children}
        {variant === 'sheet' && <DialogPrimitive.Close ref={closeRef} className="hidden" aria-hidden tabIndex={-1} />}
        {!hideCloseButton && (
          <DialogPrimitive.Close
            type="button"
            aria-label="Close dialog"
            className="hidden sm:flex absolute right-4 sm:right-5 top-4 sm:top-5 z-10 h-9 w-9 items-center justify-center rounded-full p-0 text-white/70 hover:text-white opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 disabled:pointer-events-none"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <X className="h-5 w-5 pointer-events-none" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 sm:gap-0', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight text-left', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-white/70', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
